/**
 * Top-bar language switcher. Three locales (en, pt-BR, ja) with
 * country-flag emoji icons; selection persists via
 * `useLocaleSwitch` (which already writes `relay.locale` to
 * localStorage and updates `<html lang>`).
 */

import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import { useState, type MouseEvent } from 'react';

import { useLocaleSwitch } from '@/i18n/IntlProvider';

// Order: JP first (default for new users — see IntlProvider's
// `DEFAULT` constant), then EN, then BR. The detector still
// honours browser language preference for first visit, but the
// menu always lists the supported locales in this fixed order.
const OPTIONS: Array<{ code: 'ja' | 'en' | 'pt-BR'; flag: string; label: string }> = [
  { code: 'ja', flag: '🇯🇵', label: '日本語' },
  { code: 'en', flag: '🇺🇸', label: 'English' },
  { code: 'pt-BR', flag: '🇧🇷', label: 'Português (BR)' },
];

export function LanguageMenu() {
  const { locale, setLocale } = useLocaleSwitch();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  const current = OPTIONS.find((o) => o.code === locale) ?? OPTIONS[0];

  return (
    <>
      <Tooltip title={current.label}>
        <IconButton
          color="inherit"
          aria-label={`言語を変更 (現在: ${current.label})`}
          onClick={(e: MouseEvent<HTMLElement>) => setAnchor(e.currentTarget)}
        >
          <span style={{ fontSize: '1.25rem', lineHeight: 1 }}>{current.flag}</span>
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {OPTIONS.map((opt) => (
          <MenuItem
            key={opt.code}
            selected={opt.code === locale}
            onClick={() => {
              setLocale(opt.code);
              setAnchor(null);
            }}
          >
            <ListItemIcon sx={{ minWidth: 32, fontSize: '1.1rem' }}>{opt.flag}</ListItemIcon>
            <ListItemText primary={opt.label} />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
